import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'wouter'
import { Card } from '@/components/ui/card'
import { useUser } from '@/hooks/use-user'
import { useGetActiveEntryPass } from '@/hooks/use-get-active-entry-pass'
import { usePrices } from '@/hooks/use-prices'
import { apiFetch } from '@/utils'
import { EntryPassTemplate, GuestType } from '@/constants'
import { IS_TESTNET } from '@/constants/walletConnectionConfig'
import { ChevronLeft, Loader2, Calendar, Clock, Users, User } from 'lucide-react'
import useSWR from 'swr'

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const GUEST_TYPE_LABELS: Record<GuestType, string> = {
	couples: 'Couples',
	single_ladies: 'Single Ladies',
	single_males: 'Single Males'
}

interface TaxCalculationResult {
	subtotal: number
	fees: Array<{
		type: string
		rate: number
		amount: number
		description: string
	}>
	total: number
}

function formatTime(time: string): string {
	const [hour, minute] = time.split(':').map(Number)
	return new Date(2000, 0, 1, hour, minute).toLocaleTimeString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		hour12: true
	})
}

function formatValidFor(validFor: string): string {
	const date = new Date(validFor)
	return date.toLocaleDateString('en-US', {
		weekday: 'short',
		month: 'short',
		day: 'numeric',
		year: 'numeric'
	}) + ' at ' + date.toLocaleTimeString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		hour12: true
	})
}

function formatPrice(amount: number): string {
	return Number(amount).toFixed(IS_TESTNET ? 6 : 2)
}

function getGuestTypeIcon(guestType?: GuestType) {
	if (guestType === 'couples') return <Users className="h-4 w-4" />
	return <User className="h-4 w-4" />
}

export default function ActiveEntryPassPage() {
	const [, navigate] = useLocation()
	const { walletAddress, loading, displayName, profileImageUrl } = useUser()
	const { convertUsdToToken } = usePrices()

	const {
		activeEntryPass,
		isEntryPassLoading
	} = useGetActiveEntryPass(walletAddress)

	// Only fetch templates as fallback for legacy passes without snapshot fields
	const hasSnapshotFields = !!activeEntryPass?.templateName
	const { data: templatesResponse } = useSWR<{ success: boolean; data: EntryPassTemplate[] }>(
		!hasSnapshotFields && activeEntryPass?.templateId ? '/v1/entry-passes/templates' : null,
		async () => {
			const response = await apiFetch('/v1/entry-passes/templates', {
				withCredentials: true
			})
			return response.json()
		}
	)

	// Fallback template lookup for legacy passes
	const fallbackTemplate = useMemo(() => {
		if (hasSnapshotFields) return null
		if (!activeEntryPass?.templateId || !templatesResponse?.data) return null
		return templatesResponse.data.find(t => t.id === activeEntryPass.templateId) || null
	}, [hasSnapshotFields, activeEntryPass, templatesResponse])

	// Resolved pass details — prefer snapshot, fall back to template
	const passName = activeEntryPass?.templateName || fallbackTemplate?.name || (activeEntryPass ? `Pass #${activeEntryPass.id}` : '')
	const passDayOfWeek = activeEntryPass?.dayOfWeek ?? fallbackTemplate?.dayOfWeek
	const passStartTime = activeEntryPass?.startTime || fallbackTemplate?.startTime
	const passEndTime = activeEntryPass?.endTime || fallbackTemplate?.endTime

	// Resolve price — prefer snapshot, fall back to template calculation
	const basePrice = useMemo(() => {
		if (activeEntryPass?.price) return parseFloat(activeEntryPass.price) || 0
		if (!fallbackTemplate || !activeEntryPass?.guestType) return 0
		switch (activeEntryPass.guestType) {
			case 'couples': return parseFloat(fallbackTemplate.priceCouples) || 0
			case 'single_ladies': return parseFloat(fallbackTemplate.priceSingleLadies) || 0
			case 'single_males': return parseFloat(fallbackTemplate.priceSingleMales) || 0
			default: return 0
		}
	}, [activeEntryPass, fallbackTemplate])

	// Fetch tax calculation
	const [taxCalc, setTaxCalc] = useState<TaxCalculationResult | null>(null)

	useEffect(() => {
		if (basePrice <= 0) return

		const calcTax = async () => {
			try {
				const response = await apiFetch('/v1/taxes/calculate', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						amount: basePrice,
						transactionType: 'entry_pass'
					})
				})
				const result = await response.json()
				if (result.success) {
					setTaxCalc(result.data)
				}
			} catch (error) {
				console.error('Failed to calculate tax for receipt:', error)
				setTaxCalc({
					subtotal: basePrice,
					fees: [],
					total: basePrice
				})
			}
		}
		calcTax()
	}, [basePrice])

	// Redirect if no active pass
	useEffect(() => {
		if (!isEntryPassLoading && !loading && !activeEntryPass) {
			navigate('/entry-passes', { replace: true })
		}
	}, [isEntryPassLoading, loading, activeEntryPass, navigate])

	if (isEntryPassLoading || loading) {
		return (
			<div className="min-h-screen bg-[#18181B] flex items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin text-primary" />
			</div>
		)
	}

	if (!activeEntryPass) {
		return null
	}

	const truncatedAddress = walletAddress
		? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`
		: ''

	return (
		<div className="min-h-screen bg-[#18181B]">
			<div className="max-w-5xl mx-auto px-5 pb-8">
				{/* Header */}
				<div
					role="banner"
					className="z-50 sticky top-0 bg-[#18181B] pb-[12px] backdrop-blur-[8px] h-auto flex items-center justify-start gap-2 text-white py-3"
				>
					<button
						type="button"
						aria-label="Go back"
						onClick={() => navigate('/entry-passes')}
						className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
					>
						<ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
					</button>
					<h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide">
						Entry Pass
					</h1>
				</div>

				{/* Ticket Content */}
				<Card className="w-full bg-[#2F2F32] border-0 rounded-[20px] overflow-hidden">

					{/* Member Profile Section */}
					<div className="flex flex-col items-center pt-6 pb-4 px-6">
						{profileImageUrl ? (
							<img
								src={profileImageUrl}
								alt={displayName || 'Member'}
								className="h-20 w-20 rounded-full object-cover border-2 border-white/20"
							/>
						) : (
							<div className="h-20 w-20 rounded-full bg-[#4C4C4C] flex items-center justify-center border-2 border-white/20">
								<span className="text-2xl font-bold text-white">
									{displayName ? displayName.charAt(0).toUpperCase() : '?'}
								</span>
							</div>
						)}
						<p className="text-white font-semibold text-lg mt-3">
							{displayName || 'Member'}
						</p>
						<span className="inline-flex items-center mt-2 px-4 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-bold tracking-wider uppercase">
							Active Pass
						</span>
					</div>

					{/* Divider — dashed ticket tear line */}
					<div className="relative mx-4">
						<div className="border-t-2 border-dashed border-white/15" />
						<div className="absolute -left-8 -top-3 h-6 w-6 rounded-full bg-[#18181B]" />
						<div className="absolute -right-8 -top-3 h-6 w-6 rounded-full bg-[#18181B]" />
					</div>

					{/* Pass Details Section */}
					<div className="px-6 pt-4 pb-2">
						{/* Pass Name */}
						<h2 className="text-xl font-bold text-white text-center mb-1">
							{passName}
						</h2>

						{/* Day & Time Slot Tags */}
						{(passDayOfWeek !== undefined && passStartTime && passEndTime) && (
							<div className="flex items-center justify-center gap-2 mb-4">
								<span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/10 text-[#BABABB] text-xs font-medium">
									<Calendar className="h-3 w-3" />
									{DAYS_OF_WEEK[passDayOfWeek]}
								</span>
								<span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/10 text-[#BABABB] text-xs font-medium">
									<Clock className="h-3 w-3" />
									{formatTime(passStartTime)} – {formatTime(passEndTime)}
								</span>
							</div>
						)}

						{/* Guest Type */}
						{activeEntryPass.guestType && (
							<div className="flex items-center justify-center gap-2 mb-4">
								<span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/15 text-primary text-xs font-semibold">
									{getGuestTypeIcon(activeEntryPass.guestType)}
									{GUEST_TYPE_LABELS[activeEntryPass.guestType]}
								</span>
							</div>
						)}
					</div>

					{/* Receipt Section */}
					{taxCalc && basePrice > 0 && (
						<>
							<div className="relative mx-4">
								<div className="border-t border-white/10" />
							</div>
							<div className="px-6 py-4">
								<div className="space-y-2">
									<div className="flex justify-between items-center">
										<span className="text-sm text-[#BABABB]">Subtotal</span>
										<div className="text-right">
											<span className="text-sm text-white">${formatPrice(taxCalc.subtotal)}</span>
											<span className="text-xs text-[#BABABB] ml-1.5">
												{formatPrice(convertUsdToToken(taxCalc.subtotal, 'sech'))} SECH
											</span>
										</div>
									</div>
									{taxCalc.fees.map((fee, i) => (
										<div key={i} className="flex justify-between items-center">
											<span className="text-sm text-[#BABABB]">{fee.description} ({(fee.rate * 100).toFixed(1)}%)</span>
											<div className="text-right">
												<span className="text-sm text-white">${formatPrice(fee.amount)}</span>
												<span className="text-xs text-[#BABABB] ml-1.5">
													{formatPrice(convertUsdToToken(fee.amount, 'sech'))} SECH
												</span>
											</div>
										</div>
									))}
									<div className="border-t border-white/10 pt-2 mt-2">
										<div className="flex justify-between items-center">
											<span className="text-sm font-semibold text-white">Total</span>
											<div className="text-right">
												<span className="text-sm font-semibold text-white">${formatPrice(taxCalc.total)}</span>
												<span className="text-xs text-primary font-semibold ml-1.5">
													{formatPrice(convertUsdToToken(taxCalc.total, 'sech'))} SECH
												</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</>
					)}

					{/* Footer Details */}
					<div className="relative mx-4">
						<div className="border-t border-white/10" />
					</div>
					<div className="px-6 py-4 space-y-0">
						{activeEntryPass.validFor && (
							<div className="flex justify-between items-center py-2.5">
								<span className="text-xs text-[#BABABB]">Valid Until</span>
								<span className="text-xs text-white font-medium text-right">
									{formatValidFor(activeEntryPass.validFor)}
								</span>
							</div>
						)}
						<div className="flex justify-between items-center py-2.5">
							<span className="text-xs text-[#BABABB]">Pass ID</span>
							<span className="text-xs text-white font-mono">{activeEntryPass.id}</span>
						</div>
						<div className="flex justify-between items-center py-2.5">
							<span className="text-xs text-[#BABABB]">Wallet</span>
							<span className="text-xs text-white font-mono">{truncatedAddress}</span>
						</div>
					</div>
				</Card>
			</div>
		</div>
	)
}
